import type { ReactNode } from "react";

import { cn } from "../foundations/cn.js";

export interface DescriptionItem {
  label: string;
  value: ReactNode;
  /** Mono and wrapping anywhere: a hash, an id, a seed, a number to compare. */
  mono?: boolean;
}

export interface DescriptionListProps {
  items: readonly DescriptionItem[];
  className?: string;
}

/**
 * One record's facts as label and value pairs. A value never truncates: a long hash wraps
 * anywhere, and on a phone a value drops under its label.
 */
export function DescriptionList({ items, className }: DescriptionListProps) {
  return (
    <dl className={cn("flex flex-col divide-y divide-border-subtle", className)}>
      {items.map((item) => (
        <div
          key={item.label}
          className="flex flex-col gap-1 py-2.5 first:pt-0 last:pb-0 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6"
        >
          <dt className="shrink-0 font-body text-body-b2 text-text-secondary">{item.label}</dt>
          <dd
            className={cn(
              "min-w-0 text-text-primary sm:text-right",
              item.mono
                ? "font-mono text-mono-meta break-all tabular-nums"
                : "font-body text-body-b2-strong break-words",
            )}
          >
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
